import { Button, Popover, Stack, Typography } from "@mui/material";
import { useState } from "react";
import TooltipForDisabledAction from "@entrilia/shared/components/TooltipForDisabledAction";
import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext";
import PortalAccessRow from "./PortalAccessRow";
import ResetPasswordButton from "./ResetPasswordButton";
import type { PasswordResetGate, SignInRowModel } from "./portalAccessModel";
import { recoveryStepsParagraphs, supportChatUnavailableTooltip } from "./portalAccessModel";

interface Props {
  model: SignInRowModel;
  contactName: string;
  hasPermissionsToManage: boolean;
  passwordResetGate: PasswordResetGate;
  onResetPassword: () => void;
}

const supportPrefill = (contactName: string) =>
  `Hi, I need help with the Investor Portal sign-in for our contact ${contactName}.`;

const PortalAccessSignInRow = ({
  model,
  contactName,
  hasPermissionsToManage,
  passwordResetGate,
  onResetPassword,
}: Props) => {
  const { showNewMessage, isIntercomWidgetLoaded } = useIntercomContext();

  const [recoveryStepsAnchor, setRecoveryStepsAnchor] = useState<HTMLButtonElement | null>(null);

  const handleContactSupportClick = () => {
    setRecoveryStepsAnchor(null);
    showNewMessage(supportPrefill(contactName));
  };

  const contactSupportButton = (
    <TooltipForDisabledAction
      title={isIntercomWidgetLoaded ? undefined : supportChatUnavailableTooltip}
      isActionDisabled={!isIntercomWidgetLoaded}
    >
      <Button variant="outlined" disabled={!isIntercomWidgetLoaded} onClick={handleContactSupportClick}>
        Contact Entrilia Support
      </Button>
    </TooltipForDisabledAction>
  );

  return (
    <PortalAccessRow
      label="Sign-in"
      value={model.value}
      caption={model.resetInstruction ? [model.caption, model.resetInstruction] : model.caption}
      status="problem"
      actions={
        <>
          {model.action === "contactSupport" && contactSupportButton}
          {model.action === "recoverySteps" && (
            <Button variant="outlined" onClick={(e) => setRecoveryStepsAnchor(e.currentTarget)}>
              View recovery steps
            </Button>
          )}
          {model.action === "resetPassword" && (
            <ResetPasswordButton
              gate={passwordResetGate}
              hasPermissionsToManage={hasPermissionsToManage}
              onClick={onResetPassword}
            />
          )}
        </>
      }
    >
      <Popover
        open={Boolean(recoveryStepsAnchor)}
        anchorEl={recoveryStepsAnchor}
        onClose={() => setRecoveryStepsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2, width: 360 } } }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h6">Recovery steps</Typography>
          {recoveryStepsParagraphs.map((paragraph) => (
            <Typography key={paragraph}>{paragraph}</Typography>
          ))}
          {contactSupportButton}
        </Stack>
      </Popover>
    </PortalAccessRow>
  );
};

export default PortalAccessSignInRow;
